import {
  getGetFiskalReceiptsQueryKey,
  useGetFiskalReceipts,
  useGetFiskalRegisters,
} from '@agos/api-client/fiskal'
import {
  EmployeeAuthModal,
  FISKAL_STORNO,
  T,
  formatCents,
  formatDateTime,
  parseMoneyToCents,
  printVirtualReceipt,
  tt,
  useAuth,
  type AuthActionMeta,
  lineVatLabel,
  registerIds,
  registerName,
  paymentLabel,
  OrderNumberLink,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Ban,
  FileText,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  X,
  Eye,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  canStornoOnDevice,
  driftsFromPrinted,
  filterReceipts,
  type FiskalReceipt,
  kasaLabel,
  linesAddedAfterPrint,
  receiptBreakdown,
  receiptState,
  receiptTotals,
  remedyForDrift,
  STATE_BADGES,
  stornoBlockedReason,
} from './receipt-list'

/**
 * `/fiskal-receipts` — every receipt the registers have printed.
 *
 * §14.2 and §11.4 are what shape this screen. A **virtual** receipt has no UID
 * because it never reached the tax authority's device — a valid state, and 2 177 of
 * the restored receipts are in it — and it therefore cannot be storned on the
 * device either. Legacy offers the storno button anyway and explains afterwards, in
 * a toast, once the register has already refused. This one says so first.
 *
 * Ported from legacy `pages/fiskal-receipts.tsx`.
 */

/**
 * What the two registers this port shipped with are called, until a franchise types a name over one.
 * §10A.35 leaves `fiskal_settings.label` NULL deliberately: writing these strings into rows would
 * freeze one translation into data.
 */
const SHIPPED_REGISTER_NAMES = { restauracia: tt('Kasa Reštaurácia'), rozvoz: tt('Kasa Rozvoz') }

const RECEIPT_LIMIT = 100

const STATE_TABS = [
  { id: 'all', label: tt('Všetky') },
  { id: 'valid', label: tt('Platné') },
  { id: 'virtual', label: tt('Virtuálne') },
  { id: 'storned', label: tt('Stornované') },
] as const

const FILTER_CLASSES =
  'px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function FiskalReceiptsPage() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()

  const [kasaId, setKasaId] = useState('restauracia')
  const [state, setState] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<FiskalReceipt | null>(null)
  const [stornoTarget, setStornoTarget] = useState<FiskalReceipt | null>(null)

  const { data, isLoading } = useGetFiskalReceipts({ kasaId, limit: RECEIPT_LIMIT })
  // §10A.35 — a tab per configured register, so a third one's receipts can be read at all. The two
  // the port shipped with are the fallback until the call answers, never an empty strip.
  const { data: registerData } = useGetFiskalRegisters()
  const registerTabs = useMemo(() => {
    const rows = Array.isArray(registerData)
      ? (registerData as Array<{ kasaId?: string; label?: string }>)
      : []
    return registerIds(registerData).map((id) => ({
      id,
      label: registerName(id, rows, SHIPPED_REGISTER_NAMES),
    }))
  }, [registerData])

  const receipts = useMemo(() => {
    const payload = data as { receipts?: unknown } | undefined
    const rows = payload?.receipts ?? []
    return Array.isArray(rows) ? (rows as FiskalReceipt[]) : []
  }, [data])

  const shown = useMemo(() => filterReceipts(receipts, { state, search }), [receipts, state, search])
  const totals = receiptTotals(receipts)
  const canStorno = hasPermission('fiskal') || hasPermission('fiskal_storno')

  /**
   * §9.151's seven columns, in legacy's order — §9.201 moved them into the shared table.
   *
   * Sorting where a sort means something: when it was printed, which order it belongs to, and what
   * it came to. Not on *Akcie*, and not on *Stav*, whose order is a badge's and not a value's.
   */
  const receiptColumns = useMemo<Column<FiskalReceipt>[]>(
    () => [
      {
        key: 'printedAt',
        header: tt('Dátum tlače'),
        sortable: true,
        sortValue: (receipt) => receipt.fiskalPrintedAt ?? '',
        render: (receipt) => (
          <span className="whitespace-nowrap text-muted-foreground text-xs">
            {receipt.fiskalPrintedAt ? formatDateTime(receipt.fiskalPrintedAt) : ''}
            <span className="block text-[10px]">{kasaLabel(receipt.fiskalKasaId)}</span>
          </span>
        ),
      },
      {
        key: 'order',
        header: tt('Objednávka'),
        sortable: true,
        sortValue: (receipt) => receipt.orderNumber ?? 0,
        render: (receipt) => (
          // §9.143 — the order number is the way back to the archive.
          <OrderNumberLink
            orderId={receipt.id}
            className="font-mono text-xs font-bold text-primary hover:underline"
            title={`${tt('Otvoriť v archíve')}: #${receipt.orderNumber ?? ''}`}
          >
            #{receipt.orderNumber}
          </OrderNumberLink>
        ),
      },
      {
        key: 'receiptNumber',
        header: tt('Číslo dokladu'),
        sortable: true,
        sortValue: (receipt) => receipt.fiskalReceiptNumber ?? '',
        render: (receipt) => (
          <span className="font-mono text-xs">{receipt.fiskalReceiptNumber ?? '-'}</span>
        ),
      },
      {
        key: 'total',
        header: tt('Suma'),
        headerClassName: 'text-right',
        className: 'text-right',
        sortable: true,
        sortValue: (receipt) => parseMoneyToCents(receipt.total),
        render: (receipt) => {
          const remedy = remedyForDrift(receipt)
          return (
            <span className="whitespace-nowrap">
              <span className="font-bold tabular-nums">
                {formatCents(parseMoneyToCents(receipt.total))}
              </span>
              {/* What AGOS knows and legacy does not stays under *Suma*, because that is the number
                  it qualifies. */}
              {driftsFromPrinted(receipt) && (
                <span
                  className="block text-[10px] font-semibold text-amber-500"
                  data-printed-total-drift={receipt.id}
                  title={tt('Doklad bol vytlačený na inú sumu, než akú objednávka uvádza teraz')}
                >
                  <T>doklad</T> {formatCents(parseMoneyToCents(receipt.printedTotal))}
                </span>
              )}
              {remedy !== null && (
                <span
                  className="block text-[10px] text-muted-foreground font-normal"
                  data-drift-remedy={receipt.id}
                >
                  {remedy}
                </span>
              )}
              {linesAddedAfterPrint(receipt) > 0 && (
                <span
                  className="block text-[10px] font-semibold text-amber-500"
                  data-lines-after-print={receipt.id}
                  title={tt('Objednávka dostala položky po vytlačení dokladu')}
                >
                  +{linesAddedAfterPrint(receipt)} <T>po doklade</T>
                </span>
              )}
            </span>
          )
        },
      },
      {
        key: 'payment',
        header: tt('Platba'),
        sortable: true,
        sortValue: (receipt) => paymentLabel(receipt.paymentMethod),
        render: (receipt) => (
          <span className="text-xs" data-receipt-payment={receipt.id}>
            {paymentLabel(receipt.paymentMethod)}
          </span>
        ),
      },
      {
        key: 'state',
        header: tt('Stav'),
        render: (receipt) => {
          const badge = STATE_BADGES[receiptState(receipt)]
          return <StatusBadge color={badge.color} label={badge.label} />
        },
      },
      {
        key: 'actions',
        header: tt('Akcie'),
        headerClassName: 'text-right',
        className: 'text-right',
        render: (receipt) => {
          const blocked = stornoBlockedReason(receipt)
          return (
            <span className="whitespace-nowrap">
              <button
                type="button"
                onClick={() => setDetail(receipt)}
                aria-label={`${tt('Detail dokladu')} #${receipt.orderNumber}`}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() =>
                  printVirtualReceipt(
                    {
                      orderNumber: receipt.orderNumber,
                      issuedAt: receipt.fiskalPrintedAt,
                      paymentMethod: receipt.paymentMethod,
                      total: receipt.total,
                      vatRate: receipt.vatRate,
                      vatBase: receipt.vatBase,
                      vatAmount: receipt.vatAmount,
                      items: receipt.items,
                    },
                    Date.now(),
                  )
                }
                aria-label={`${tt('Vytlačiť')} #${receipt.orderNumber}`}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
              </button>
              {canStorno && (
                <button
                  type="button"
                  onClick={() => setStornoTarget(receipt)}
                  disabled={!canStornoOnDevice(receipt)}
                  title={blocked ?? tt('Stornovať fiškálny doklad')}
                  aria-label={`${tt('Stornovať')} #${receipt.orderNumber}`}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Ban className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          )
        },
      },
    ],
    [canStorno],
  )

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetFiskalReceiptsQueryKey() })

  const stornoMeta: AuthActionMeta | null = stornoTarget
    ? {
        orderId: stornoTarget.id ?? 0,
        orderNumber: stornoTarget.orderNumber ?? '',
        orderTotalCents: parseMoneyToCents(stornoTarget.total),
        newStatus: FISKAL_STORNO,
      }
    : null

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Fiškálne doklady')}
        description={tt('Doklady vytlačené kasami.')}
        icon={<Receipt className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={refresh}
            aria-label={tt('Obnoviť')}
            className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />

      <TabGroup
        tabs={registerTabs}
        active={kasaId}
        onChange={setKasaId}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={tt('Dokladov')} value={String(totals.count)} icon={FileText} color="violet" />
        <StatCard
          label={tt('Obrat')}
          value={formatCents(totals.grossCents)}
          icon={Receipt}
          color="emerald"
        />
        <StatCard label="DPH" value={formatCents(totals.vatCents)} icon={FileText} color="blue" />
        <StatCard
          label={tt('Virtuálnych')}
          value={String(totals.virtualCount)}
          icon={AlertTriangle}
          color="amber"
        />
      </div>

      {totals.stornoCount > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {/* A storned receipt was reversed, so it is counted but not banked.
              Legacy sums every row into the takings. */}
          <T>Stornované doklady sa počítajú, ale nie sú v obrate:</T> {totals.stornoCount}
        </p>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Číslo objednávky, dokladu, položka...')}
            aria-label={tt('Hľadať doklad')}
            className={cn(FILTER_CLASSES, 'w-full pl-9')}
          />
        </div>
        <select
          value={state}
          onChange={(event) => setState(event.target.value)}
          aria-label={tt('Stav dokladu')}
          className={FILTER_CLASSES}
        >
          {STATE_TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {[1, 2, 3, 4].map((placeholder) => (
            <div
              key={placeholder}
              className="h-14 rounded-xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Receipt className="w-10 h-10" />}
            title={tt('Žiadne doklady')}
            description={tt('Táto kasa v tomto filtri nič nevytlačila.')}
          />
        </div>
      ) : (
        <div data-receipts>
          {/* §9.201 - the shared `DataTable`, not a hand-rolled one. Twenty-odd screens use it, and
              this table wrote its own head, padding and hover - so it was not styled differently, it
              was a different component, without sorting, paging or the card layout a narrow screen
              gets. §9.151's seven columns and their order are unchanged; what moved is the frame.

              `rowAttributes` is why the conversion was possible without rewriting the specs:
              `[data-receipt-row]` is how `fiskal-receipts.spec.ts` finds a row. */}
          <DataTable
            columns={receiptColumns}
            data={shown}
            // `id` is optional on the wire; the index is the fallback so a row without one is
            // still keyed rather than crashing the list it belongs to.
            rowKey={(receipt, index) => receipt.id ?? index}
            rowAttributes={(receipt) => ({ 'data-receipt-row': receipt.id })}
            emptyMessage={tt('Žiadne doklady')}
            compact
          />
        </div>
      )}

      {detail && <ReceiptDetail receipt={detail} onClose={() => setDetail(null)} />}

      {stornoMeta && (
        <EmployeeAuthModal
          meta={stornoMeta}
          onClose={() => setStornoTarget(null)}
          onSuccess={(_confirmedBy, response) => {
            setStornoTarget(null)
            refresh()
            // The storno response is not one of the typed order responses, so it is read by name.
            const storno = response as { message?: unknown; virtual?: unknown } | undefined
            const message = typeof storno?.message === 'string' ? storno.message : undefined
            if (storno?.virtual === true) {
              toast({
                title: tt('Storno označené lokálne'),
                description:
                  message ?? tt('Doklad bol vystavený vo virtuálnom režime - kasa o ňom nevie.'),
                duration: 8000,
              })
              return
            }
            toast({
              title: tt('Storno vykonané'),
              description: message ?? tt('Fiškálny doklad bol stornovaný.'),
            })
          }}
        />
      )}
    </div>
  )
}

function ReceiptDetail({
  receipt,
  onClose,
}: {
  receipt: FiskalReceipt
  onClose: () => void
}) {
  const breakdown = receiptBreakdown(receipt)
  const badge = STATE_BADGES[receiptState(receipt)]

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Receipt className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            {/* §9.143 - a tax document, and the order it was printed for. The list row itself
              * is already a button that opens this modal, so the link belongs here. */}
            <p className="font-bold">
              <OrderNumberLink
                orderId={receipt.id}
                className="text-primary hover:underline"
                title={`${tt('Otvoriť v archíve')}: #${receipt.orderNumber ?? ''}`}
              >
                #{receipt.orderNumber}
              </OrderNumberLink>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {receipt.fiskalReceiptNumber ?? tt('bez čísla dokladu')} ·{' '}
              {kasaLabel(receipt.fiskalKasaId)}
            </p>
          </div>
          <StatusBadge color={badge.color} label={badge.label} />
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {receipt.fiskalStornoAt && (
            <p className="text-xs text-red-500">
              <T>Stornovaný</T> {formatDateTime(receipt.fiskalStornoAt)}
              {receipt.fiskalStornoBy ? ` · ${receipt.fiskalStornoBy}` : ''}
            </p>
          )}

          <div className="space-y-1">
            {(receipt.items ?? []).map((line, index) => (
              <div key={`${line.productName}-${index}`} className="flex justify-between text-sm">
                <span className="min-w-0 truncate">
                  <span className="text-primary font-bold">{line.quantity}×</span>{' '}
                  {line.productName}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {/* §9.5's DPH bullet. A receipt is the document a customer is handed and a tax
                      office reads, so the letter each line was registered under is stated on every
                      one - unlike the order detail, which draws it only on a mixed-rate order. The
                      register prints the letter, not the percentage, and a reconciliation groups by
                      it, which is what FB-192 cost when a 19 % line went out under A. */}
                  {lineVatLabel(line) !== '' && (
                    <span className="text-[11px] text-muted-foreground" data-receipt-line-vat>
                      {lineVatLabel(line)}
                    </span>
                  )}
                  <span className="tabular-nums">
                    {formatCents(parseMoneyToCents(line.totalPrice))}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-border pt-2 space-y-1 text-sm">
            <Row label={tt('Medzisúčet')} value={formatCents(breakdown.subtotalCents)} />
            {breakdown.discountCents > 0 && (
              <Row label={tt('Zľava')} value={`−${formatCents(breakdown.discountCents)}`} />
            )}
            {breakdown.deliveryCents > 0 && (
              <Row label={tt('Dovoz')} value={formatCents(breakdown.deliveryCents)} />
            )}
            <Row
              label={`${tt('Základ DPH')} (${receipt.vatRate ?? 0}%)`}
              value={formatCents(breakdown.vatBaseCents)}
            />
            <Row label={`DPH (${receipt.vatRate ?? 0}%)`} value={formatCents(breakdown.vatCents)} />
            <div className="flex justify-between font-bold pt-1 border-t border-border">
              <span>
                <T>Celkom</T>
              </span>
              <span className="text-primary tabular-nums">
                {formatCents(breakdown.totalCents)}
              </span>
            </div>
          </div>

          {breakdown.inconsistent && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              {/* Surfaced rather than rounded away - a receipt whose tax lines do
                  not add up to its own total is a document somebody has to see. */}
              <T>Základ a DPH nedávajú dokopy celkovú sumu tohto dokladu.</T>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}
